import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, MapPin, Star } from 'lucide-react';
import { useLang } from '../i18n';
import { PriceOnRequest } from './PriceOnRequest';
import { EstimatePrice } from './EstimatePrice';

export const AccommodationTypeBadge = ({ acc, t, testid }) => {
  if (acc.verified_partner) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-eco-green/10 px-2.5 py-0.5 text-[11px] font-semibold text-eco-green">
        <BadgeCheck size={11} /> {t('acc.verified')}
      </span>
    );
  }
  if (acc.partner_type === 'affiliate' || acc.partner_type === 'reference') {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-eco-ink/5 px-2.5 py-0.5 text-[11px] font-semibold text-eco-ink/50">
        {t(`acc.ptype.${acc.partner_type}`)}
      </span>
    );
  }
  return null;
};

export const AccommodationRating = ({ rating, isNew, t, testid }) => {
  if (rating) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1.5 text-sm font-semibold text-eco-ink">
        <Star size={14} className="fill-eco-gold text-eco-gold" /> {rating.avg}/5
        <span className="text-xs font-normal text-eco-ink/50">· {rating.count} {t('acc.reviews')}</span>
      </span>
    );
  }
  if (isNew) {
    return <span data-testid={`${testid}-new`} className="inline-flex rounded-full bg-eco-gold/15 px-2.5 py-0.5 text-[11px] font-bold text-eco-gold">{t('acc.new')}</span>;
  }
  return null;
};

export const AccommodationCard = ({ acc, delay = 0 }) => {
  const { t } = useLang();
  const testid = `accommodation-card-${acc.slug}`;
  return (
    <article data-testid={testid} className="group eco-card flex h-full flex-col overflow-hidden !p-0">
      <Link to={`/accommodations/${acc.slug}`} className="relative block aspect-[16/9] overflow-hidden bg-eco-cream" data-testid={`${testid}-image-link`}>
        {(acc.cover_image || acc.main_image) ? (
          <img src={acc.cover_image || acc.main_image} alt={acc.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" data-testid={`${testid}-image`} />
        ) : (
          <span className="grid h-full w-full place-items-center text-eco-ink/25"><MapPin size={28} /></span>
        )}
        {acc.official_category && (
          <span data-testid={`${testid}-category`} className="absolute top-3 left-3 rounded-full bg-eco-deep/85 px-3 py-1 text-[11px] font-bold tracking-wide text-eco-gold backdrop-blur-sm">{acc.official_category}</span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-eco-gold">{acc.island}{acc.country === 'CV' ? ' · Cabo Verde' : ''}</p>
        <h3 className="eco-card-title mt-1.5">{acc.name}</h3>
        {acc.property_type && <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-eco-ink/50">{t(`stay.type.${acc.property_type}`)}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <AccommodationRating rating={acc.rating} isNew={acc.is_new} t={t} testid={`${testid}-rating`} />
          <AccommodationTypeBadge acc={acc} t={t} testid={`${testid}-ptype`} />
        </div>
        {(acc.short_description || acc.description) && <p className="eco-secondary mt-3 line-clamp-2 text-eco-ink/60">{acc.short_description || acc.description}</p>}
        {acc.amenities?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {acc.amenities.slice(0, 4).map((a) => <span key={a} className="rounded-full bg-eco-cream px-2.5 py-0.5 text-[11px] font-medium text-eco-ink/60">{a}</span>)}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-eco-ink/10 pt-4">
          {acc.price_from != null ? (
            <p data-testid={`${testid}-price`} className="text-sm text-eco-ink/55">{t('acc.from')} {(acc.currency || 'EUR') === 'EUR' ? <EstimatePrice testid={`${testid}-price-value`} amountEur={acc.price_from} compact /> : <strong className="eco-number text-base text-eco-green">{acc.price_from.toFixed(2)} {acc.currency}</strong>}</p>
          ) : (
            <PriceOnRequest compact testid={`${testid}-price`} />
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Link to={`/accommodations/${acc.slug}`} data-testid={`${testid}-view`} className="eco-btn-secondary justify-center !min-h-10 whitespace-nowrap px-3 text-xs">{t('acc.view')}</Link>
          <Link to={`/request-stay?stay=${encodeURIComponent(acc.slug)}`} data-testid={`${testid}-request`} className="eco-btn-primary justify-center !min-h-10 whitespace-nowrap px-3 text-xs">{t('cta.checkstay')} <ArrowRight size={13} /></Link>
        </div>
      </div>
    </article>
  );
};
